/** Turning a picked file into a small profile picture, all in the browser (D-44). */

const MAX_SOURCE_BYTES = 8 * 1024 * 1024
const OUTPUT_SIZE = 256
const JPEG_QUALITY = 0.85

/** Mirrors the server ceiling: the picture travels as an inline data URL. */
export const AVATAR_MAX_CHARS = 64 * 1024

/**
 * Shrink a chosen image to a 256×256 square JPEG data URL.
 *
 * Done here, not on the server: a phone photo is several megabytes, and the account row
 * should not carry that. Centre-cropped so every avatar lines up in the bar and the table.
 */
export async function compressAvatar(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Choose an image file — PNG, JPEG or WebP.')
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('That image is larger than 8 MB — pick a smaller one.')
  }

  const bitmap = await loadBitmap(file)
  const side = Math.min(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('This browser cannot resize images.')
  }
  context.drawImage(
    bitmap,
    (bitmap.width - side) / 2,
    (bitmap.height - side) / 2,
    side,
    side,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  )

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
  if (dataUrl.length > AVATAR_MAX_CHARS) {
    throw new Error('That picture is still too large after resizing — try a simpler image.')
  }
  return dataUrl
}

/** `createImageBitmap` is the modern path; decoding through an `<img>` is the fallback. */
async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file)
  }
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('That image could not be read.'))
      image.src = url
    })
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}
